import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  StyledTableCellWithoutLeft,
  StyledTableCellWithoutLeftRight,
} from 'routes/Main/style';
import Form from 'components/Form';

import useTableForm from '../../hooks/useTableForm';
import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

import { ReactComponent as MMSE9pic } from 'assets/MMSE_9_pic.svg';

const contentLabel = [
  {
    id: 1,
    ko: 'Category',
    desc: [
      '1. Mỗi câu trả lời đúng sẽ được 1 điểm',
      '2. Mỗi câu trả lời đúng sẽ được 1 điểm',
      '3. "Tôi sẽ đặt tên cho ba từ/vật thể và bạn cần lặp lại chúng. Sau đó hãy nhớ chúng bởi vì tôi sẽ yêu cầu bạn đặt lại tên chúng sau." ',
    ],
    scores: [5, 5, 3],
    ids: [0, 1, 2],
  },
  {
    id: 2,
    ko: 'Registration',
    desc: ['4. Đánh vần ngược lại từ WORLD " Câu trả lời: D-L-R-O-W'],
    scores: [5],
    ids: [3],
  },
  {
    id: 3,
    ko: 'Attention',
    desc: ['5. Đánh vần ngược lại từ WORLD " Câu trả lời: D-L-R-O-W'],
    scores: [3],
    ids: [4],
  },
  {
    id: 4,
    ko: 'Language',
    desc: [
      '6. "Đồ vật này là gì?" - Cho xem đồng hồ đeo tay.',
      '7. "Đồ vật này là gì?" -  Cho xem cây bút chì.',
      '8. "Lặp lại cụm từ này: No ifs, ands, or buts',
      '9. "Đọc những từ này và sau đó làm theo những gì viết trên đó." (Hãy đưa cho bệnh nhân một tờ giấy ghi”Nhắm mắt lại”.)',
    ],
    scores: [1, 1, 1, 1],
    ids: [5, 6, 7, 8],
  },
  {
    id: 5,
    ko: 'Recall',
    desc: ['10. "Lấy tờ giấy ở tay phải/trái, gấp làm đôi, và đặt xuống sàn."'],
    scores: [3],
    ids: [9],
  },
  {
    id: 6,
    ko: 'Understanding\n/Judgement',
    desc: [
      '11. "Nghĩ và viết một câu hoàn chỉnh trên một mảnh giấy." (Câu phải chứa một động từ và danh từ.)',
      '12. "Hãy vẽ lại hình này."',
    ],
    scores: [1, 1],
    ids: [10, 11],
  },
];

const questions1 = [
  '“Năm nay là năm nào?”',
  '“Hiện đang là mùa gì?”',
  '“Hiện đang là tháng mấy”',
  '“Hôm nay là ngày mấy?”',
  '“Hôm nay là thứ mấy?”',
];

const questions2 = [
  '“Chúng ta đang ở quốc gia nào?”',
  '“Chúng ta đang ở tỉnh/ thành phố nào?”',
  '“Chúng ta đang ở quận/ huyện nào”',
  '“Địa chỉ nhà bạn ở đâu/ Tên của tòa nhà này là gì?”',
  '“Chúng ta đang ở tầng mấy/ Phòng nào?”',
];

const scoreLabel = [
  { score: '30-26', label: 'Không suy giảm nhận thức' },
  { score: '25-20', label: 'Suy giảm nhận thức nhẹ ' },
  { score: '19-10', label: 'Suy giảm nhận thức trung bình' },
  { score: '9-0', label: 'Suy giảm nhận thức nặng' },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
}

const MMSEContents = (props: Props) => {
  const { palette } = useTheme();
  const { disabled, getValues, register } = props;

  const min = 0;
  const max = 5;

  const [input1, setInput1] = useState(
    getValues('score[0]') ? Number(getValues('score[0]')) : 0
  );
  const [input2, setInput2] = useState(
    getValues('score[1]') ? Number(getValues('score[1]')) : 0
  );
  const [input3, setInput3] = useState(
    getValues('score[2]') ? Number(getValues('score[2]')) : 0
  );
  const [input4, setInput4] = useState(
    getValues('score[3]') ? Number(getValues('score[3]')) : 0
  );
  const [input5, setInput5] = useState(
    getValues('score[4]') ? Number(getValues('score[4]')) : 0
  );
  const [input6, setInput6] = useState(
    getValues('score[5]') ? Number(getValues('score[5]')) : 0
  );
  const [input7, setInput7] = useState(
    getValues('score[6]') ? Number(getValues('score[6]')) : 0
  );
  const [input8, setInput8] = useState(
    getValues('score[7]') ? Number(getValues('score[7]')) : 0
  );
  const [input9, setInput9] = useState(
    getValues('score[8]') ? Number(getValues('score[8]')) : 0
  );
  const [input10, setInput10] = useState(
    getValues('score[9]') ? Number(getValues('score[9]')) : 0
  );
  const [input11, setInput11] = useState(
    getValues('score[10]') ? Number(getValues('score[10]')) : 0
  );
  const [input12, setInput12] = useState(
    getValues('score[11]') ? Number(getValues('score[11]]')) : 0
  );

  const total =
    input1 +
    input2 +
    input3 +
    input4 +
    input5 +
    input6 +
    input7 +
    input8 +
    input9 +
    input10 +
    input11 +
    input12;

  return (
    <Box sx={{ width: 'max-content', margin: '50px auto' }}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <StyledTableCellWithoutLeftRight
              align="left"
              sx={{ padding: '16px' }}
            >
              Câu hỏi
            </StyledTableCellWithoutLeftRight>
            <StyledTableCellWithoutLeftRight
              align="center"
              sx={{ padding: '16px' }}
            >
              Điểm
            </StyledTableCellWithoutLeftRight>
          </TableRow>
        </TableHead>
        <TableBody>
          {contentLabel.map(
            (
              content: {
                id: number;
                ko: string;
                desc: string[];
                scores: number[];
                ids: number[];
              },
              _
            ) => (
              <TableRow>
                <StyledTableCellWithoutLeftRight>
                  {content.desc.map((v, i) => {
                    return (
                      <TableRow
                        sx={{
                          height: '48px',
                          lineHeight: '48px',
                          borderBottom:
                            i !== content.desc.length - 1
                              ? '1px solid lightgray'
                              : '',
                        }}
                      >
                        <Box
                          sx={{
                            paddingLeft: '16px',
                            minWidth: '1100px',
                            fontSize: '12px',
                          }}
                        >
                          {v}
                          <br />
                          {v === '12. "Hãy vẽ lại hình này."' && <MMSE9pic />}
                          {v === '1. Mỗi câu trả lời đúng sẽ được 1 điểm' && (
                            <Box
                              sx={{ marginTop: '-15px', marginBottom: '10px' }}
                            >
                              {questions1.map(a => (
                                <Typography fontSize={'12px'}>• {a}</Typography>
                              ))}
                            </Box>
                          )}
                          {v === '2. Mỗi câu trả lời đúng sẽ được 1 điểm' && (
                            <Box
                              sx={{ marginTop: '-15px', marginBottom: '10px' }}
                            >
                              {questions2.map(a => (
                                <Typography fontSize={'12px'}>• {a}</Typography>
                              ))}
                            </Box>
                          )}
                          {v ===
                            '3. "Tôi sẽ đặt tên cho ba từ/vật thể và bạn cần lặp lại chúng. Sau đó hãy nhớ chúng bởi vì tôi sẽ yêu cầu bạn đặt lại tên chúng sau." ' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                margin: '-18px 0 10px 13px',
                              }}
                            >
                              (VD: QUẢ BÓNG - XE HƠI - ĐÀN ÔNG / Táo - Đồng xu -
                              Bàn)
                            </Typography>
                          )}
                          {v ===
                            '10. "Lấy tờ giấy ở tay phải/trái, gấp làm đôi, và đặt xuống sàn."' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                margin: '-18px 0 10px 13px',
                              }}
                            >
                              (Đưa cho bệnh nhân một mẩu giấy và tính 1 điểm cho
                              mỗi hành động được thực hiện.)
                            </Typography>
                          )}
                        </Box>
                      </TableRow>
                    );
                  })}
                </StyledTableCellWithoutLeftRight>
                <StyledTableCellWithoutLeftRight>
                  {content.scores.map((score, i) => (
                    <TableRow
                      sx={{
                        height:
                          content.id === 6 && i === 1
                            ? '171.5px'
                            : (content.id === 1 && i === 0) ||
                              (content.id === 1 && i === 1)
                            ? '133px'
                            : (content.desc[2] && content.id === 1) ||
                              content.desc[5]
                            ? '58px'
                            : '48px',
                        lineHeight:
                          content.id === 6 && i === 1
                            ? '171.5px'
                            : (content.id === 1 && i === 0) ||
                              (content.id === 1 && i === 1)
                            ? '133px'
                            : (content.desc[2] && content.id === 1) ||
                              content.desc[5]
                            ? '58px'
                            : '48px',
                        textAlign: 'center',
                        borderBottom:
                          i !== content.desc.length - 1
                            ? '1px solid lightgray'
                            : '',
                      }}
                    >
                      <Box
                        sx={{
                          width: '120px',
                          height:
                            content.id === 6 && i === 1
                              ? '171.5px'
                              : (content.id === 1 && i === 0) ||
                                (content.id === 1 && i === 1)
                              ? '133px'
                              : (content.desc[2] && content.id === 1) ||
                                content.desc[5]
                              ? '58px'
                              : '48px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Form.MuiTextField
                          disabled={disabled}
                          required={false}
                          type="number"
                          inputProps={{ min, max }}
                          value={
                            content.desc[i].includes('1. Mỗi')
                              ? input1
                              : content.desc[i].includes(String('2. Mỗi'))
                              ? input2
                              : content.desc[i].includes(String('3.'))
                              ? input3
                              : content.desc[i].includes(String('4.'))
                              ? input4
                              : content.desc[i].includes(String('5.'))
                              ? input5
                              : content.desc[i].includes(String('6.'))
                              ? input6
                              : content.desc[i].includes(String('7.'))
                              ? input7
                              : content.desc[i].includes(String('8.'))
                              ? input8
                              : content.desc[i].includes(String('9.'))
                              ? input9
                              : content.desc[i].includes(String('10.'))
                              ? input10
                              : content.desc[i].includes(String('11.'))
                              ? input11
                              : content.desc[i].includes(String('12.'))
                              ? input12
                              : 0
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                /{score}
                              </InputAdornment>
                            ),
                          }}
                          {...register(`score[${content.ids[i]}]`, {
                            onChange: e => {
                              var value = parseInt(e.target.value, 10);
                              // console.log('✅', content.desc[i]);

                              let max = { score };

                              if (value >= max.score) value = max.score;
                              if (value < min) value = min;

                              if (content.desc[i].includes('1. Mỗi'))
                                setInput1(value);
                              else if (content.desc[i].includes('2. Mỗi'))
                                setInput2(value);
                              if (content.desc[i].includes('3.'))
                                setInput3(value);
                              if (content.desc[i].includes('4.'))
                                setInput4(value);
                              if (content.desc[i].includes('5.'))
                                setInput5(value);
                              if (content.desc[i].includes('6.'))
                                setInput6(value);
                              if (content.desc[i].includes('7.'))
                                setInput7(value);
                              if (content.desc[i].includes('8.'))
                                setInput8(value);
                              if (content.desc[i].includes('9.'))
                                setInput9(value);
                              if (content.desc[i].includes('10.'))
                                setInput10(value);
                              if (content.desc[i].includes('11.'))
                                setInput11(value);
                              if (content.desc[i].includes('12.'))
                                setInput12(value);
                            },
                          })}
                          // {...register(`score[${content.ids[i]}]`)}
                        />
                      </Box>
                    </TableRow>
                  ))}
                </StyledTableCellWithoutLeftRight>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-end'}
        sx={{ marginTop: '20px' }}
      >
        <Typography
          gutterBottom
          minWidth={115}
          fontWeight={700}
          variant="subtitle1"
        >
          Tổng : {total}
        </Typography>
        <Typography
          minWidth={115}
          variant="caption"
          sx={{ color: `${palette.primary.dark}` }}
        >
          {scoreLabel.map(({ score, label }) => (
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                {label} :
              </Box>
              {score}
            </Typography>
          ))}
        </Typography>
      </Box>
    </Box>
  );
};

export default MMSEContents;
